import type { SVGIcon } from "@tql/ui-components-react/kendo/common";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Skeleton } from "@tql/ui-components-react/kendo/indicators";

import { formatNumber } from "~/shared/utils/string-fns.ts";

import styles from "./indicator-card.module.css";

type IndicatorCardProps = {
	label: string;
	count?: number | null;
	icon?: SVGIcon;
	type: "base" | "info" | "error" | "error-strong";
	size?: "small" | "large";
	border?: boolean;
	flex?: number;
	isLoading?: boolean;
	onClick?: () => void;
};

const typeClassMap = {
	base: styles.indicatorCardBase,
	info: styles.indicatorCardInfo,
	error: styles.indicatorCardError,
	"error-strong": styles.indicatorCardErrorStrong,
} as const;

export const IndicatorCard = ({
	label,
	count,
	icon,
	type,
	size = "large",
	border = false,
	flex = 1,
	isLoading = false,
	onClick,
}: IndicatorCardProps) => {
	const sizeClass = size === "small" ? styles.indicatorCardSmall : "";
	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (onClick && (event.key === "Enter" || event.key === " ")) {
			event.preventDefault();
			onClick();
		}
	};

	if (isLoading) {
		return (
			<div
				className={`${styles.indicatorCard} ${styles.indicatorCardBase} ${sizeClass} ${border ? styles.border : ""}`}
				style={{ flex: flex }}
			>
				<Skeleton
					shape={"rectangle"}
					style={{ width: 32, height: 24, borderRadius: 4 }}
				/>
				<Skeleton shape={"text"} style={{ width: "60%" }} />
			</div>
		);
	}

	return (
		<div
			className={`${styles.indicatorCard} ${typeClassMap[type]} ${sizeClass} ${border ? styles.border : ""} ${onClick ? styles.clickable : ""}`}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			role={onClick ? "button" : undefined}
			style={{ flex: flex }}
			tabIndex={onClick ? 0 : undefined}
		>
			{icon ? (
				<span className={styles.indicatorCardCount}>
					<SvgIcon icon={icon} size={"large"} />
				</span>
			) : (
				<span
					className={`${styles.indicatorCardCount} ${count === 0 ? styles.indicatorCardCountZero : ""}`}
				>
					{count == null ? "-" : formatNumber(count)}
				</span>
			)}
			{label}
		</div>
	);
};
